import Point from 'ol/geom/Point';
import proj4 from 'proj4';
import Feature from 'ol/Feature';
import { Geometry, LineString } from 'ol/geom';
export interface IFeatureClassification {
    text: Feature[];
    poly: Feature[];
}

// Functions related to turning GeoJSON features into OpenLayers features

const epsg3857proj4 = '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs';

const calculateScaleForPoint = (feature: Feature) => {
    const geometry: Geometry = feature.getGeometry() as Geometry;

    let coordinate: Coordinate;

    if (feature.get('type') === 'Point' || feature.get('TYPE') === 'TEXT') {
        const point: Point = geometry as Point;
        coordinate = point.getFirstCoordinate();
    } else if (feature.get('type') === 'LineString') {
        const lineString: LineString = geometry as LineString;
        coordinate = lineString.getCoordinates()[0];
    } else {
        return 1;
    }

    const [_, lat] = proj4(epsg3857proj4).inverse(coordinate);
    const latitudeInRadians: number = lat * (Math.PI / 180);
    return 1 / Math.cos(latitudeInRadians);
};

const replacer = (_, value) => {
    if (!value || !value.geometry) {
        return value;
    }

    let type;
    const rawType = value.type;
    let geometry = value.geometry;
    if (rawType === 1) {
        type = 'MultiPoint';
        if (geometry.length === 1) {
            type = 'Point';
            geometry = geometry[0];
        }
    } else if (rawType === 2) {
        type = 'MultiLineString';
        if (geometry.length === 1) {
            type = 'LineString';
            geometry = geometry[0];
        }
    } else if (rawType === 3) {
        type = 'Polygon';
        if (geometry.length > 1) {
            type = 'MultiPolygon';
            geometry = [geometry];
        }
    }

    return {
        type: 'Feature',
        geometry: {
            type: type,
            coordinates: geometry,
        },
        properties: value.tags,
    };
};

const extractTextFeaturesFromFeatureCollection = (featureCollection: Feature[]): IFeatureClassification => {
    const result: IFeatureClassification = featureCollection.reduce(
        (result, feature) => {
            if (feature.get('TYPE') === 'TEXT') {
                result.text.push(feature);
            } else {
                result.poly.push(feature);
            }

            return result;
        },
        { text: [], poly: [] }
    );

    return result;
};

const getCoordinatesFromGeometry = (geometry: any): number[][] | null => {
    const flatCoords = geometry.flatCoordinates_;
    if (!flatCoords) {
        return null;
    }
    const stride = geometry.stride_ || 2;
    const coordinates: number[][] = [];
    for (let i = 0; i < flatCoords.length; i += stride) {
        coordinates.push([flatCoords[i], flatCoords[i + 1]]);
    }
    return coordinates;
};

const getPointAtDistance = (coordinates: number[][], distance: number): { point: number[]; angle: number } | null => {
    let accumulatedDistance = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
        const start = coordinates[i];
        const end = coordinates[i + 1];
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const segmentLength = Math.sqrt(dx * dx + dy * dy);

        if (accumulatedDistance + segmentLength >= distance) {
            const remainingDistance = distance - accumulatedDistance;
            const ratio = remainingDistance / segmentLength;
            return {
                point: [start[0] + dx * ratio, start[1] + dy * ratio],
                angle: Math.atan2(dy, dx),
            };
        }
        accumulatedDistance += segmentLength;
    }
    return null;
};

const getLineLength = (coordinates: number[][]): number => {
    let length = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
        const dx = coordinates[i + 1][0] - coordinates[i][0];
        const dy = coordinates[i + 1][1] - coordinates[i][1];
        length += Math.sqrt(dx * dx + dy * dy);
    }
    return length;
};

const normalizeTextAngle = (angle: number): number => {
    while (angle > Math.PI) angle -= 2 * Math.PI;
    while (angle < -Math.PI) angle += 2 * Math.PI;

    if (angle > Math.PI / 2) {
        return angle - Math.PI;
    } else if (angle < -Math.PI / 2) {
        return angle + Math.PI;
    }
    return angle;
};

export const GeoJsonUtils = {
    extractTextFeaturesFromFeatureCollection,
    replacer,
    calculateScaleForPoint,
    getCoordinatesFromGeometry,
    getPointAtDistance,
    getLineLength,
    normalizeTextAngle,
};
